import {alias, createModelSchema, list, reference} from "serializr";
import {BaseObject} from "@/common/BaseObject";
import {Schema} from "@/common/Schema";

import {Currency} from "../currency/Currency";
import {GuardConfig} from "../guard/GuardConfig";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {Tile} from "@/modules/redactor/models/utils/Tile";
import {RewardBunch} from "@/modules/redactor/models/reward/RewardBunch";
import {StashBonusBunchGroup} from "@/modules/redactor/models/stash/StashBonusBunchGroup";
import {ProductGroupKind} from "@/modules/redactor/models/product/ProductGroupKind";
import {StoreBundleConfig} from "@/modules/redactor/models/product/StoreBundleConfig";
import {UuidUtils} from "@/common/UuidUtils";

export class ProductModel extends BaseObject {

    static schema() {
        return reference("uid", (uid, cb) => {
            return cb(null, RedactorUtils.getProduct(uid));
        });
    }

    constructor(group = ProductGroupKind.NONE) {
        super();

        this._id = null;
        this._key = null;
        this._name = null;
        this._segment = null;
        this._group = group;

        this._guard = new GuardConfig();

        this._display = 0;
        this._discount = 0;
        this._quantity = 0;
        this._sticker = 0;
        this._consumable = false;
        this._stashSupport = false;
        this._segmentStart = false;

        this._freeuse = 0;
        this._freetime = 0;
        this._resettime = 0;
        this._resetPrice = [];

        this._place = new Tile(0, 0);

        this._marker = 0;
        this._percent = 0;
        this._priority = 0;

        this._payment = 0;

        this._price = [];
        this._survey = null;
        this._bundles = [];

        this._adPlacement = null;
        this._rewardBunch = null;
        this._stashBonusBunch = null;
        this._sharedGlobal = false;
    }

    get id() {
        return this._id;
    }

    set id(value) {
        this._id = value;
    }

    get key() {
        return this._key;
    }

    set key(value) {
        this._key = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    get segment() {
      return this._segment;
    }

    set segment(value) {
      this._segment = value;
    }

    get group() {
        return this._group;
    }

    set group(value) {
        this._group = value;
    }

    get guard() {
        return this._guard;
    }

    set guard(value) {
        this._guard = value;
    }

    get place() {
        return this._place;
    }

    set place(value) {
        this._place = value;
    }

    get marker() {
        return this._marker;
    }

    set marker(value) {
        this._marker = value;
    }

    get percent() {
        return this._percent;
    }

    set percent(value) {
        this._percent = value;
    }

    get priority() {
        return this._priority;
    }

    set priority(value) {
        this._priority = value;
    }

    get display() {
        return this._display;
    }

    set display(value) {
        this._display = value;
    }

    get discount() {
        return this._discount;
    }

    set discount(value) {
        this._discount = value;
    }

    get sticker() {
        return this._sticker;
    }

    set sticker(value) {
        this._sticker = value;
    }

    get consumable() {
        return this._consumable;
    }

    set consumable(value) {
        this._consumable = value;
    }

    get stashSupport() {
        return this._stashSupport;
    }

    set stashSupport(value) {
        this._stashSupport = value;
    }

    get segmentStart() {
        return this._segmentStart;
    }

    set segmentStart(value) {
        this._segmentStart = value;
    }

    get freeuse() {
        return this._freeuse;
    }

    set freeuse(value) {
        this._freeuse = value;
    }

    get freetime() {
        return this._freetime;
    }

    set freetime(value) {
        this._freetime = value;
    }

    get resettime() {
        return this._resettime;
    }

    set resettime(value) {
        this._resettime = value;
    }

    get resetPrice() {
        return this._resetPrice;
    }

    set resetPrice(value) {
        this._resetPrice = value;
    }

    get quantity() {
        return this._quantity;
    }

    set quantity(value) {
        this._quantity = value;
    }

    get rewardBunch() {
        return this._rewardBunch;
    }

    set rewardBunch(value) {
        this._rewardBunch = value;
    }

    get stashBonusBunch() {
        return this._stashBonusBunch;
    }

    set stashBonusBunch(value) {
        this._stashBonusBunch = value;
    }

    get payment() {
        return this._payment;
    }

    set payment(value) {
        this._payment = value;
    }

    get price() {
        return this._price;
    }

    set price(value) {
        this._price = value;
    }

    get survey() {
        return this._survey;
    }

    set survey(value) {
        this._survey = value;
    }

    get bundles() {
        return this._bundles;
    }

    set bundles(value) {
        this._bundles = value;
    }

    get adPlacement() {
        return this._adPlacement;
    }

    set adPlacement(value) {
        this._adPlacement = value;
    }

    get sharedGlobal() {
        return this._sharedGlobal;
    }

    set sharedGlobal(value) {
        this._sharedGlobal = value;
    }

    valid() {

        if (!this.id) {
            return false;
        }

        if (!ProductGroupKind.is(this.group) || ProductGroupKind.isNone(this.group)) {
            return false;
        }

        if (!Number.isInteger(this.discount)) {
            return false;
        }

        if (!Number.isInteger(this.freeuse) || this.freeuse < 0) {
            return false;
        }

        if (!Number.isInteger(this.freetime) || this.freetime < 0) {
            return false;
        }

        if (!Number.isInteger(this.resettime) || this.resettime < 0) {
            return false;
        }

        if (this.place && !this.place.valid()) {
            return false;
        }

        if (!Number.isInteger(this.marker) || this.marker < 0) {
            return false;
        }

        if (!Number.isInteger(this.sticker) || this.sticker < 0) {
            return false;
        }

        if (!Number.isInteger(this.percent) || this.percent < 0 || this.percent > 100) {
            return false;
        }

        if (!Number.isInteger(this.priority)) {
            return false;
        }

        if (this.guard && !this.guard.valid()) {
            return false;
        }

        if (this.rewardBunch && !this.rewardBunch.valid()) {
            return false;
        }

        if (this.payment === 0 /* LOCAL */) {
            if (!this.price || this.price.some(v => !v || !v.valid())) {
                return false;
            }
        } else if (this.payment === 1 /* STORE */) {

            if (!this.bundles || this.bundles.length === 0) {
                return false;
            }

            if (this.bundles.some(v => !v || !v.valid())) {
                return false;
            }

            let active = this.bundles.reduce((count, bundle) => {
                return count + (bundle.active ? 1 : 0);
            }, 0);

            if (active !== 1) {
                return false;
            }
        } else if (this.payment === 2 /* SURVEY */) {
            if (!this.survey || this.survey.length === 0) {
                return false;
            }
        } else if (this.payment === 3 /* AD */) {
            if (!this.adPlacement || !RedactorUtils.getAdPlacement(this.adPlacement)) {
                return false;
            }
        } else {
            return false;
        }

        if (this.resetPrice && this.resetPrice.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.stashBonusBunch && !this.stashBonusBunch.valid()) {
            return false;
        }

        const map = RedactorUtils.getCurrentMapKind();
        if(this.sharedGlobal && !this.global && map.key !== "map_global") {
            return false;
        }

        return true;
    }

    copy() {
        let value = super.copy();
        value._id = null;
        value._enabled = false;
        return value;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new ProductModel());
        } else if (target instanceof ProductModel) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(ProductModel, {
    _id: alias("id", Schema.primitive()),
    _key: alias("key", Schema.primitive()),
    _name: alias("name", Schema.primitive()),
    _segment: alias("segment", Schema.primitive()),
    _group: alias("group", ProductGroupKind.schema()),
    _guard: alias("guard", GuardConfig.schema()),

    _freeuse: alias("freeuse", Schema.primitive()),
    _freetime: alias("freetime", Schema.primitive()),
    _resettime: alias("resettime", Schema.primitive()),
    _resetPrice: alias("reset_price", list(Currency.schema())),

    _display: alias("display", Schema.primitive()),
    _discount: alias("discount", Schema.primitive()),
    _quantity: alias("quantity", Schema.primitive()),
    _consumable: alias("consumable", Schema.primitive()),
    _stashSupport: alias("stash_support", Schema.primitive()),
    _segmentStart: alias("segment_start", Schema.primitive()),
    _sticker: alias("sticker", Schema.primitive()),

    _place: alias("place", Tile.schema()),
    _marker: alias("marker", Schema.primitive()),
    _percent: alias("percent", Schema.primitive()),
    _priority: alias("priority", Schema.primitive()),

    _payment: alias("payment", Schema.primitive()),

    _price: alias("price", list(Currency.schema())),
    _survey: alias("survey", Schema.primitive()),
    _bundles: alias("bundles", list(StoreBundleConfig.schema())),

    _adPlacement: alias("ad_placement", Schema.primitive()),
    _rewardBunch: alias("reward_bunch", RewardBunch.schema()),
    _stashBonusBunch: alias("stash_bonus_bunch", StashBonusBunchGroup.schema()),
    _sharedGlobal: alias("shared", Schema.primitive())
}, (context) => {
    let json = context.json;

    if (json["bundles"] === undefined && json["store_id"] !== undefined) {
        let config = {};
        config.uid = UuidUtils.generate();
        config.active = 1;
        config.bundle = json["store_id"];
        config.points = json["payer_points"];
        json["bundles"] = [config];
    }

    return new ProductModel();
});